import {
  getIsAmdMarkdownSectionalEditorEnabled,
  useIsAmdMarkdownSectionalEditorEnabled,
} from '@/features/flags';
import { useQueryIntegrationOnboardingSessions } from '@/hooks/query/integrations/general/onboarding-session/useQueryIntegrationOnboardingSessions';
import { COMPLETED_ONBOARDING_SESSIONS_PARAMS } from '@heidi/integrations/connected-apps/contracts';
import { EHRProvider } from '@heidi/schema/ml-scribe';
import { useMemo } from 'react';

/**
 * Returns true when the AdvancedMD MARKDOWN sectional editor should be active
 * for the current session. Requires:
 *
 * - `release-integrations-enable-amd-new-markdown-sectional-editor` ON (default)
 * - `ehrProvider === AdvancedMD`, or no provider passed and the shared
 *   completed-onboarding-sessions cache shows an AdvancedMD connection
 *
 * Returns false for non-AdvancedMD providers when a provider is supplied. The
 * flag hook still runs (Rules of Hooks).
 */
export const useAmdMarkdownSectionalEditorEnabled = (ehrProvider?: EHRProvider) => {
  const isFlagEnabled = useIsAmdMarkdownSectionalEditorEnabled();
  const { data: hasAmdOnboardingSession } = useQueryIntegrationOnboardingSessions({
    params: COMPLETED_ONBOARDING_SESSIONS_PARAMS,
    enabled: false,
    select: (data) =>
      data.sessions.some(
        (session) => (session.provider as EHRProvider) === EHRProvider.AdvancedMD
      ),
  });

  return useMemo(() => {
    if (!isFlagEnabled) return false;
    if (ehrProvider !== undefined) {
      return ehrProvider === EHRProvider.AdvancedMD;
    }
    return hasAmdOnboardingSession ?? false;
  }, [ehrProvider, isFlagEnabled, hasAmdOnboardingSession]);
};

/**
 * Imperative (non-hook) resolver for contexts outside React render — mirrors
 * `useAmdMarkdownSectionalEditorEnabled` when the provider is known. The flag is
 * a simple boolean with no session/date dependency, so no cache prefetch is needed.
 */
export const isAmdMarkdownSectionalEditorEnabled = (
  ehrProvider?: EHRProvider
): boolean => {
  if (ehrProvider !== EHRProvider.AdvancedMD) return false;
  return getIsAmdMarkdownSectionalEditorEnabled();
};
